import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DashboardConfig, WidgetInstance } from "@/models/dashboard/Dashboard";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";
import type { WidgetType, WidgetConfig } from "@/models/dashboard/WidgetConfig";

interface DashboardStore {
  // Current dashboard state
  selectedDashboardId: string | null;
  isEditMode: boolean;
  isDirty: boolean;
  localConfig: DashboardConfig | null;
  /** The widget the editor panel is open on (LAR-726). */
  editingWidgetId: string | null;
  /** The edited widget as it was when editing began; "Fortryd ændringer i widgetten" puts it back. */
  editingWidgetSnapshot: WidgetInstance | null;
  /** The panel shows the "Tilføj widget" gallery instead of an editor. */
  isWidgetGalleryOpen: boolean;

  // Actions
  setSelectedDashboard: (id: string | null) => void;
  setEditMode: (enabled: boolean) => void;
  setLocalConfig: (config: DashboardConfig | null) => void;
  markDirty: () => void;
  clearDirty: () => void;

  // Widget manipulation (works on localConfig)
  /** Adds the widget at the bottom of the grid and returns its id (null without a draft). */
  addWidget: (
    type: WidgetType,
    name: string,
    config: WidgetConfig,
    size?: { w: number; h: number }
  ) => string | null;
  updateWidget: (widgetId: string, updates: Partial<WidgetInstance>) => void;
  removeWidget: (widgetId: string) => void;
  updateWidgets: (widgets: WidgetInstance[]) => void;

  // Widget editor panel (LAR-726)
  openWidgetGallery: () => void;
  beginWidgetEditing: (widgetId: string) => void;
  restoreWidgetSnapshot: () => void;
  endWidgetEditing: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  selectedDashboardId: null,
  isEditMode: false,
  isDirty: false,
  localConfig: null,
  editingWidgetId: null,
  editingWidgetSnapshot: null,
  isWidgetGalleryOpen: false,
};

const CLOSED_WIDGET_PANEL = {
  editingWidgetId: null,
  editingWidgetSnapshot: null,
  isWidgetGalleryOpen: false,
};

const useDashboardStore = create<DashboardStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSelectedDashboard: (id) => set({ selectedDashboardId: id }),

      setEditMode: (enabled) =>
        set({ isEditMode: enabled, ...(!enabled && CLOSED_WIDGET_PANEL) }),

      // Switching dashboard, Annuller and Gem all load a config; none of them keeps the panel open.
      setLocalConfig: (config) =>
        set({ localConfig: config, isDirty: false, ...CLOSED_WIDGET_PANEL }),

      markDirty: () => set({ isDirty: true }),

      clearDirty: () => set({ isDirty: false }),

      addWidget: (type, name, config, size = { w: 6, h: 4 }) => {
        const { localConfig } = get();
        if (!localConfig) return null;

        const newWidget: WidgetInstance = {
          id: uuidv4(),
          type,
          name,
          position: { x: 0, y: Infinity, ...size }, // Add to bottom
          config,
        };

        set({
          localConfig: {
            ...localConfig,
            widgets: [...localConfig.widgets, newWidget],
          },
          isDirty: true,
        });
        return newWidget.id;
      },

      updateWidget: (widgetId, updates) => {
        const { localConfig } = get();
        if (!localConfig) return;

        set({
          localConfig: {
            ...localConfig,
            widgets: localConfig.widgets.map((w) =>
              w.id === widgetId ? { ...w, ...updates } : w
            ),
          },
          isDirty: true,
        });
      },

      removeWidget: (widgetId) => {
        const { localConfig, editingWidgetId } = get();
        if (!localConfig) return;

        set({
          localConfig: {
            ...localConfig,
            widgets: localConfig.widgets.filter((w) => w.id !== widgetId),
          },
          isDirty: true,
          // The panel never shows a widget that is no longer on the grid.
          ...(widgetId === editingWidgetId && CLOSED_WIDGET_PANEL),
        });
      },

      updateWidgets: (widgets) => {
        const { localConfig } = get();
        if (!localConfig) return;

        set({
          localConfig: {
            ...localConfig,
            widgets,
          },
          isDirty: true,
        });
      },

      openWidgetGallery: () =>
        set({ ...CLOSED_WIDGET_PANEL, isWidgetGalleryOpen: true }),

      beginWidgetEditing: (widgetId) => {
        const widget = get().localConfig?.widgets.find(
          (w) => w.id === widgetId
        );
        if (!widget) return;

        set({
          editingWidgetId: widgetId,
          editingWidgetSnapshot: structuredClone(widget),
          isWidgetGalleryOpen: false,
        });
      },

      restoreWidgetSnapshot: () => {
        const { localConfig, editingWidgetSnapshot: snapshot } = get();
        if (!localConfig || !snapshot) return;

        set({
          localConfig: {
            ...localConfig,
            widgets: localConfig.widgets.map((w) =>
              w.id === snapshot.id
                ? {
                    ...w,
                    name: snapshot.name,
                    config: structuredClone(snapshot.config),
                  }
                : w
            ),
          },
        });
      },

      endWidgetEditing: () => set(CLOSED_WIDGET_PANEL),

      reset: () => set(initialState),
    }),
    {
      name: "dashboard-store",
      partialize: (state) => ({
        selectedDashboardId: state.selectedDashboardId,
      }),
    }
  )
);

export default useDashboardStore;
